import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import {
  REPORT_PRIORITIES,
  REPORT_STATUS_GROUPS,
  REPORT_TARGET_TYPES,
  ReportPriority,
  ReportSortMode,
  ReportStatusGroup,
  ReportTargetType,
} from '../../services/moderation-constants';

export interface ReportFilterData {
  status: ReportStatusGroup[];
  priority: ReportPriority[];
  targetType: ReportTargetType[];
  sortMode: ReportSortMode;
}

@Component({
  selector: 'app-report-filter-modal',
  templateUrl: './report-filter-modal.component.html',
  styleUrls: ['./report-filter-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonCheckbox,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonRadioGroup,
    IonRadio,
    IonFooter,
    IonGrid,
    IonRow,
    IonCol,
  ],
})
export class ReportFilterModalComponent implements OnInit {
  private modalCtrl: ModalController = inject(ModalController);
  @Input() filters?: Partial<ReportFilterData> | null;

  public readonly statusOptions: Array<{ value: ReportStatusGroup; label: string; checked: boolean }> = Object.keys(
    REPORT_STATUS_GROUPS
  ).map((value) => ({ value: value as ReportStatusGroup, label: this.toLabel(value), checked: false }));
  public readonly priorityOptions: Array<{ value: ReportPriority; label: string; checked: boolean }> =
    REPORT_PRIORITIES.map((value) => ({ value, label: this.toLabel(value), checked: false }));
  public readonly targetTypeOptions: Array<{ value: ReportTargetType; label: string; checked: boolean }> =
    REPORT_TARGET_TYPES.map((value) => ({ value, label: value, checked: false }));
  public sortMode: ReportFilterData['sortMode'] = 'newest';

  ngOnInit() {
    this.applyInitialSelections();
  }

  applyFilters() {
    this.modalCtrl.dismiss({
      filters: {
        status: this.selectedValues(this.statusOptions),
        priority: this.selectedValues(this.priorityOptions),
        targetType: this.selectedValues(this.targetTypeOptions),
        sortMode: this.sortMode,
      },
    });
  }

  public updateOption(option: { checked: boolean }, event: CustomEvent) {
    option.checked = Boolean(event.detail.checked);
  }

  private selectedValues<T extends string>(options: Array<{ value: T; checked: boolean }>): T[] {
    return options.filter((option) => option.checked).map((option) => option.value);
  }

  private applyInitialSelections() {
    const filters = this.filters;
    if (!filters) return;

    this.setCheckedValues(this.statusOptions, filters.status);
    this.setCheckedValues(this.priorityOptions, filters.priority);
    this.setCheckedValues(this.targetTypeOptions, filters.targetType);
    if (filters.sortMode) {
      this.sortMode = filters.sortMode;
    }
  }

  private setCheckedValues<T extends string>(options: Array<{ value: T; checked: boolean }>, values?: T[]) {
    const selected = new Set(values || []);
    options.forEach((option) => {
      option.checked = selected.has(option.value);
    });
  }

  private toLabel(value: string): string {
    if (value === 'resolved') return 'Resolved';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  clearFilters() {
    this.modalCtrl.dismiss({ clear: true });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
